<template>
	<GlassCard class="blood-sugar-tracker p-3">
		<h2>Blood Sugar</h2>
		<div v-if="hasToken() && !userStore.isViewingUser() && isOnline">
			<div class="mb-3">
				<label for="sugarDateTime" class="form-label">Date and Time</label>
				<input
					type="datetime-local"
					id="sugarDateTime"
					v-model="newRecord.dateTime"
					class="form-control" />
			</div>
			<div class="mb-3">
				<label for="sugarLevel" class="form-label">Blood Sugar (mg/dL)</label>
				<input
					type="number"
					id="sugarLevel"
					v-model="newRecord.level"
					class="form-control"
					step="1" />
			</div>
			<div class="d-grid gap-2">
				<button @click="addRecord" class="btn btn-primary">Submit</button>
			</div>
		</div>
		<div class="mt-3">
			<canvas id="sugarChart"></canvas>
		</div>
	</GlassCard>
</template>

<script setup>
	import { ref, onMounted } from "vue"
	import GlassCard from "./GlassCard.vue"
	import Chart from "chart.js/auto"
	import Swal from "sweetalert2"
	import moment from "moment-timezone"
	import { useTokenStore } from "@/stores/token"
	// import { hasTokenIssue, handleTokenIssue } from "@/utils/tokenIssue"
	import { fetchWithAuth } from "@/utils/api-client"
	import { useUserStore } from "@/stores/user"
	import { isOnline } from "@/utils/networkBus"
	import * as SubscriptionHelper from "@/utils/subscriptionHelper"

	const userStore = useUserStore()

	const records = ref([])
	const newRecord = ref({
		dateTime: moment().format("YYYY-MM-DD hh:mm A"),
		level: "",
	})
	const chartRef = ref(null)

	const tokenStore = useTokenStore()
	const { hasToken } = tokenStore

	const fetchRecordsForYear = async (year) => {
		try {
			const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
			const response = await fetchWithAuth(`/api/bloodsugars/${year}`, {
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					"User-Timezone": tz,
					// Authorization: `Bearer ${tokenStore.token}`,
				},
			})
			// if (hasTokenIssue(response)) {
			// 	handleTokenIssue(response)
			// } else {
			if (!response.ok) throw new Error("Network response was not ok")
			const data = await response.json()
			records.value = data
			// records.value = data.map((record) => ({
			// 	dateTime: moment.unix(record.timestamp).format("YYYY-MM-DD HH:mm"),
			// 	timestamp: record.timestamp,
			// 	id: record.id,
			// 	level: record.level,
			// }))
			updateChart()
			// }
		} catch (error) {
			console.error("Failed to fetch records:", error)
		}
	}

	const addRecord = async () => {
		if (!newRecord.value.dateTime || !newRecord.value.level) {
			Swal.fire({
				icon: "error",
				title: "Oops...",
				text: "Please fill out the date and blood sugar level fields.",
			})
			return
		}

		const feature = "Add blood sugar"
		if (!SubscriptionHelper.isFreeAccount()) {
			SubscriptionHelper.showLockedModal(feature)
		} else {
			try {
				const ts = moment(newRecord.value.dateTime).utc().unix()
				const existingRecord = records.value.find(
					(record) => record.timestamp === ts
				)
				if (existingRecord) {
					Swal.fire({
						icon: "error",
						title: "Oops...",
						text: "A record for this time already exists.",
					})
					return
				}
				const response = await fetchWithAuth("/api/bloodsugars", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						// Authorization: `Bearer ${tokenStore.token}`,
					},
					body: JSON.stringify({ timestamp: ts, level: newRecord.value.level }),
				})
				// if (hasTokenIssue(response)) {
				// 	handleTokenIssue(response)
				// } else {

				if (
					!(await SubscriptionHelper.requestCheckSubscription(
						response,
						feature
					))
				)
					return

				if (!response.ok) {
					throw new Error("Could not add record")
				}

				const data = await response.json()
				console.log(newRecord.value, data)
				records.value.push({
					...{
						dateTime: moment.unix(ts).format("YYYY-MM-DD hh:mm A"),
						timestamp: ts,
						id: data.id,
						level: newRecord.value.level,
					},
				})
				records.value.sort((a, b) => a.timestamp - b.timestamp)
				newRecord.value = {
					dateTime: moment().format("YYYY-MM-DD hh:mm A"),
					level: "",
				}
				updateChart()
				// }
			} catch (error) {
				alert(error.message)
				console.error("Failed to add record:", error)
			}
		}
	}

	const deleteRecord = async (id) => {
		try {
			const response = await fetchWithAuth(`/api/bloodsugars/${id}`, {
				method: "DELETE",
				headers: {
					"Content-Type": "application/json",
					// Authorization: `Bearer ${tokenStore.token}`,
				},
			})
			// if (hasTokenIssue(response)) {
			// 	handleTokenIssue(response)
			// } else {
			if (!response.ok) throw new Error("Network response was not ok")
			const index = records.value.findIndex((record) => record.id === id)
			if (index > -1) {
				records.value.splice(index, 1) // Remove the record from the local array
				updateChart()
			}
			// }
		} catch (error) {
			console.error("Failed to delete the record:", error)
		}
	}

	const onChartClick = (event, elements) => {
		if (!hasToken()) return
		if (userStore.isViewingUser()) return
		if (elements.length > 0) {
			const index = elements[0].index
			const record = records.value[index]
			Swal.fire({
				title: "What would you like to do?",
				showDenyButton: true,
				showCancelButton: true,
				confirmButtonText: "Edit",
				denyButtonText: `Delete`,
			}).then((result) => {
				if (result.isConfirmed) {
					Swal.fire({
						title: "Enter new Level:",
						input: "number",
						inputValue: record.level,
						inputAttributes: {
							step: "0.1",
							min: "0",
						},
						showCancelButton: true,
					}).then((inputValue) => {
						if (inputValue.value) {
							const updatedLevel = parseFloat(inputValue.value)
							updateRecord(record.id, updatedLevel)
						}
					})
				} else if (result.isDenied) {
					Swal.fire({
						title: "Are you sure you want to delete this record?",
						showCancelButton: true,
						confirmButtonText: "Delete",
					}).then((result) => {
						if (result.isConfirmed) {
							deleteRecord(record.id)
						}
					})
				}
			})
		}
	}

	const updateRecord = async (id, level) => {
		try {
			const response = await fetchWithAuth(`/api/bloodsugars/${id}`, {
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					// Authorization: `Bearer ${tokenStore.token}`,
				},
				body: JSON.stringify({ level }),
			})
			// if (hasTokenIssue(response)) {
			// 	handleTokenIssue(response)
			// } else {
			if (!response.ok) throw new Error("Network response was not ok")
			// Optionally, fetch the updated records list or directly update the local state
			const index = records.value.findIndex((record) => record.id === id)
			if (index > -1) {
				records.value[index].level = level
				updateChart()
			}
			// }
		} catch (error) {
			console.error("Failed to update the record:", error)
		}
	}

	const updateChart = () => {
		const chartData = {
			datasets: [
				{
					label: "Blood Sugar Level",
					data: records.value.map((record) => ({
						x: record.dateTime,
						y: record.level,
					})),
					backgroundColor: "rgba(75, 192, 192, 0.2)",
					borderColor: "rgba(75, 192, 192, 1)",
					borderWidth: 1,
					tension: 0.4,
				},
			],
		}
		if (chartRef.value) {
			chartRef.value.destroy()
		}
		const ctx = document.getElementById("sugarChart").getContext("2d")
		chartRef.value = new Chart(ctx, {
			type: "line",
			data: chartData,
			options: {
				onClick: onChartClick,
				scales: {
					y: {
						beginAtZero: false,
						ticks: {
							color: "white",
						},
					},
					x: {
						ticks: {
							color: "white",
						},
					},
				},
				plugins: {
					legend: {
						labels: {
							color: "white",
						},
					},
				},
				responsive: true,
				// maintainAspectRatio: true,
			},
		})
	}

	onMounted(() => {
		const year = moment().startOf("year").format("YYYY-MM")
		fetchRecordsForYear(year)
	})
</script>

<style lang="less" scoped>
	.blood-sugar-tracker {
		max-width: 600px;
		margin: auto;
	}
</style>
